import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import Pagination from '../../components/ui/Pagination.jsx'
import { Input, Select } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { fetchAllPaginated, indexById } from '../../lib/paginate.js'
import { formatCurrency, formatDate } from '../../lib/format.js'
import { invoiceStatus, isOutgoing } from './invoiceStatus.js'

/*
 * Invoices list — inventory §2. Paginated, filterable table of outgoing +
 * incoming invoices for the current company. Server-side pagination + status/
 * type filters; client-side search + `overdue` filter (kept from the legacy
 * behaviour). Customer/supplier names are resolved via id→name maps so rows
 * never show "#id". Destructive actions go through the internal confirm modal
 * (never window.confirm), per DESIGN.md §8.
 */
const TYPE_OPTIONS = ['all', 'sent', 'received']
const STATUS_OPTIONS = ['all', 'sent', 'received', 'paid', 'overdue', 'cancelled']

export default function InvoicesListPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { confirm, toast } = useFeedback()
  const { currentCompanyId } = useCompany()

  const [invoices, setInvoices] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [custMap, setCustMap] = useState(new Map())
  const [supMap, setSupMap] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState(null) // { key, dir } — server-side over the whole set

  // Click a column header: first click sorts ascending, next descending, then off.
  const toggleSort = (key) => {
    setPage(1)
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null))
  }

  // Debounce the search box, then fetch server-side over the WHOLE dataset.
  useEffect(() => {
    const id = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1) }, 300)
    return () => clearTimeout(id)
  }, [search])

  // Load id→name maps once per company (covers all pages to avoid "#id").
  useEffect(() => {
    if (!currentCompanyId) return undefined
    let active = true
    Promise.all([
      fetchAllPaginated(api, '/customers/', 'customers').catch(() => []),
      fetchAllPaginated(api, '/suppliers/', 'suppliers').catch(() => []),
    ]).then(([cust, sup]) => {
      if (!active) return
      setCustMap(indexById(cust))
      setSupMap(indexById(sup))
    })
    return () => { active = false }
  }, [currentCompanyId])

  const fetchInvoices = useCallback(() => {
    if (!currentCompanyId) return undefined
    setLoading(true)
    setError(false)
    // Everything is filtered server-side over the full dataset (search, type,
    // status, overdue) so results aren't limited to the current page.
    const params = { page, per_page: perPage }
    if (typeFilter !== 'all') params.type = typeFilter
    if (statusFilter === 'overdue') params.overdue = 'true'
    else if (statusFilter !== 'all') params.status = statusFilter
    if (debouncedSearch) params.search = debouncedSearch
    if (sort) params.sort = `${sort.key}_${sort.dir}`
    return api.get('/invoices/', { params })
      .then(({ data }) => {
        setInvoices(data?.invoices || [])
        setPagination(data?.pagination || { page: 1, pages: 1, total: 0 })
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [currentCompanyId, page, perPage, typeFilter, statusFilter, debouncedSearch, sort])

  useEffect(() => { fetchInvoices() }, [fetchInvoices])

  const partnerName = useCallback((inv) => {
    if (isOutgoing(inv)) return custMap.get(inv.customer_id)?.name || `#${inv.customer_id}`
    return supMap.get(inv.supplier_id)?.name || `#${inv.supplier_id}`
  }, [custMap, supMap])

  const rows = invoices

  const onFilterChange = (setter) => (e) => { setter(e.target.value); setPage(1) }

  const markPaid = async (inv, paid) => {
    try {
      await api.post(`/invoices/${inv.id}/${paid ? 'mark-as-paid' : 'mark-as-unpaid'}`)
      toast(t(paid ? 'invoices.toast.markedPaid' : 'invoices.toast.markedUnpaid'), 'success')
      fetchInvoices()
    } catch {
      toast(t('invoices.toast.error'), 'error')
    }
  }

  const removeDraft = async (inv) => {
    const ok = await confirm({
      tone: 'danger',
      title: t('invoices.confirmDelete.title'),
      message: t('invoices.confirmDelete.message', { number: inv.invoice_number || `#${inv.id}` }),
      confirmLabel: t('invoices.confirmDelete.confirm'),
      cancelLabel: t('common.cancel'),
    })
    if (!ok) return
    try {
      await api.delete(`/invoices/${inv.id}`)
      toast(t('invoices.toast.deleted'), 'success')
      fetchInvoices()
    } catch {
      toast(t('invoices.toast.error'), 'error')
    }
  }

  const hasFilters = typeFilter !== 'all' || statusFilter !== 'all' || search.trim() !== ''
  const action = (
    <Button variant="primary" icon="plus" onClick={() => navigate('/app/invoices/new')}>{t('invoices.new')}</Button>
  )

  const th = (label, align = 'left', sortKey = null) => {
    const active = sortKey && sort?.key === sortKey
    const base = { textAlign: align, padding: '12px 18px', fontSize: 11.5, fontWeight: 600, color: active ? 'var(--color-primary)' : 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }
    if (!sortKey) return <th style={base}>{label}</th>
    return (
      <th style={{ ...base, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(sortKey)} title={t('common.sort')}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
          {label}
          <span style={{ opacity: active ? 1 : 0.35, fontSize: 10 }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
        </span>
      </th>
    )
  }

  return (
    <Page title={t('invoices.title')} action={action}>
      {/* Filters card */}
      <Card style={{ marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }}><Icon name="search" size={17} /></span>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('invoices.search')}
            style={{ height: 42, paddingLeft: 38 }} aria-label={t('common.search')} />
        </div>
        <Select value={typeFilter} onChange={onFilterChange(setTypeFilter)} aria-label={t('invoices.filters.type')} style={{ height: 42, width: 'auto', minWidth: 150 }}>
          {TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o === 'all' ? t('invoices.filters.typeAll') : t(`invoices.direction.${o}`)}</option>)}
        </Select>
        <Select value={statusFilter} onChange={onFilterChange(setStatusFilter)} aria-label={t('invoices.filters.status')} style={{ height: 42, width: 'auto', minWidth: 160 }}>
          <option value="all">{t('invoices.filters.statusAll')}</option>
          {STATUS_OPTIONS.filter((o) => o !== 'all').map((o) => <option key={o} value={o}>{t(`invoices.status.${o === 'sent' ? 'sent' : o}`)}</option>)}
        </Select>
      </Card>

      <Card padding={0}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-danger)' }}>{t('invoices.toast.error')}</div>
        ) : rows.length === 0 ? (
          <EmptyState t={t} hasFilters={hasFilters} onNew={() => navigate('/app/invoices/new')} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead>
                <tr style={{ background: 'var(--color-bg-lilac)' }}>
                  {th(t('invoices.columns.type'))}
                  {th(t('invoices.columns.number'), 'left', 'invoice_number')}
                  {th(t('invoices.columns.dueDate'), 'left', 'due_date')}
                  {th(t('invoices.columns.partner'))}
                  {th(t('invoices.columns.amount'), 'right', 'amount')}
                  {th(t('invoices.columns.status'), 'left', 'status')}
                  {th(t('common.actions'), 'right')}
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => {
                  const st = invoiceStatus(inv)
                  const out = isOutgoing(inv)
                  const go = () => navigate(`/app/invoices/${inv.id}`)
                  return (
                    <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border-row)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-lilac)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '')}>
                      <td style={{ padding: '12px 18px' }} onClick={go}>
                        <Badge tone={out ? 'info' : 'neutral'}>{out ? t('invoices.direction.sent') : t('invoices.direction.received')}</Badge>
                      </td>
                      <td style={{ padding: '12px 18px', fontWeight: 600, color: 'var(--color-ink)', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={go}>
                        {inv.invoice_number || `#${inv.id}`}
                        {inv.is_credit_note && <span style={{ marginLeft: 8 }}><Badge tone="danger">{t('invoices.status.creditNote')}</Badge></span>}
                      </td>
                      <td style={{ padding: '12px 18px', color: 'var(--color-text)', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={go}>
                        {inv.is_credit_note ? '—' : formatDate(inv.due_date)}
                      </td>
                      <td style={{ padding: '12px 18px', color: 'var(--color-ink)', cursor: 'pointer', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={go}>
                        {partnerName(inv)}
                      </td>
                      <td style={{ padding: '12px 18px', textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={go}>
                        {formatCurrency(inv.amount_including_tax, inv.currency || 'EUR')}
                      </td>
                      <td style={{ padding: '12px 18px' }} onClick={go}><Badge tone={st.tone}>{t(st.key)}</Badge></td>
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
                          <IconButton icon="eye" title={t('common.view')} onClick={go} />
                          {!inv.is_draft && inv.status !== 'paid' && inv.status !== 'cancelled' && (
                            <IconButton icon="check" title={t('invoices.actions.markPaid')} variant="success" onClick={() => markPaid(inv, true)} />
                          )}
                          {!inv.is_draft && inv.status === 'paid' && (
                            <IconButton icon="undo" title={t('invoices.actions.markUnpaid')} onClick={() => markPaid(inv, false)} />
                          )}
                          {inv.is_draft && (
                            <IconButton icon="trash" title={t('invoices.actions.delete')} variant="danger" onClick={() => removeDraft(inv)} />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div style={{ padding: '0 18px 16px' }}>
            <Pagination page={pagination.page} totalPages={pagination.pages} total={pagination.total} unit={t('invoices.unit')}
              perPage={perPage} onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              onPerPage={(n) => { setPerPage(n); setPage(1) }} />
          </div>
        )}
      </Card>
    </Page>
  )
}

function EmptyState({ t, hasFilters, onNew }) {
  return (
    <div style={{ padding: '56px 28px', textAlign: 'center' }}>
      <div style={{ color: 'var(--color-text-muted)', display: 'flex', justifyContent: 'center', marginBottom: 14 }}><Icon name="invoice" size={40} strokeWidth={1.5} /></div>
      <div style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 17, color: 'var(--color-ink)', marginBottom: 6 }}>
        {hasFilters ? t('invoices.empty.filtered') : t('invoices.empty.title')}
      </div>
      {!hasFilters && (
        <>
          <div style={{ color: 'var(--color-text)', marginBottom: 18 }}>{t('invoices.empty.message')}</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="primary" icon="plus" onClick={onNew}>{t('invoices.new')}</Button>
          </div>
        </>
      )}
    </div>
  )
}
